// Relation specs — a company/product/employee reference defined ONCE, applied
// to any FK with context (Section 6 of docs/plans/schema-codegen-architecture.md).
//
// A spec owns: its display component, the columns it needs on the TARGET table
// (as `db.<target>.*` objects), the `data` accessor, the entity-select kind
// (for the inline-edit/form widget), and the set of context knobs it accepts.
//
// `spec.on(fk, ctx)` yields a `PresentationField`:
//   - `component` + `data` come from the spec
//   - the relation BINDING (fk column + target columns + alias) drives the
//     derived PostgREST select — PostgREST infers the target table from the FK
//   - `edit` is an entity-select of the spec's `entityKind`, merged with `ctx`
//
// This replaces `pickRef` / `defineJoins` / `refDeps` / `composeDeps` and every
// cross-entity schema import. One definition of "a company chip" per app.

import type { ComponentType } from "react";
import { db, type AnyColumnRef } from "./graph";
import type { PresentationField, RelationBinding } from "@ds/config/presentation";
import { relationAlias, fieldLabelKey } from "@ds/config/presentation";
import type { EditFieldSpec } from "@ds/editable/editFieldSpec";

import { entitySelectFilter } from "@ds/tables/helpers/sharedFilterDefs";
import { CompanyChip, type CompanyChipProps } from "@ds/domain/CompanyChip";
import { CompanyCard, type CompanyCardProps } from "@ds/domain/CompanyCard";
import { ProductChip, type ProductChipProps } from "@ds/domain/ProductChip";
import { EmployeeChip, type EmployeeChipProps } from "@ds/domain/EmployeeChip";

// ─── Context knobs accepted by `.on(fk, ctx)` ──────────────────────────────
//
// A subset of the `entity-select` edit spec — the per-application-site filters
// (requireNip, category, role, …) plus presentation flags. Merged into the
// derived `edit.field`.

export interface RelationContext {
  /** company: only companies with a NIP. */
  requireNip?: boolean;
  /** company: category filter. */
  category?: string;
  /** employee: role filter. */
  role?: import("@shared/types").EmployeeRole;
  /** entity-select placeholder. */
  placeholder?: string;
  /** Mark the FK edit required. */
  required?: boolean;
  /** Render the field read-only (no entity-select edit emitted). */
  readOnly?: boolean;
  /**
   * Override the embed alias. By default `relationAlias(fk.column)` is used
   * (e.g. `applicant_company_id` → `applicant_company`). When the default
   * alias collides with a scalar column of the same name on the parent
   * table (PostgREST returns the embed object, masking the scalar — see
   * `edpr_requests.applicant_company` text vs `applicant_company_id` FK),
   * pass a non-colliding alias here (e.g. `"applicant_company_ref"`).
   */
  alias?: string;
}

// ─── Spec definition + resolved spec ───────────────────────────────────────

interface RelationSpecDef<P> {
  component: ComponentType<P>;
  /** Maps the embedded related record to the component's data props. */
  data: (related: unknown) => Partial<P>;
  /** Columns to select on the TARGET table — `db.<target>.*` objects. */
  cols: (graph: typeof db) => AnyColumnRef[];
  /** Entity-select kind for the inline-edit / form widget. */
  entityKind: import("@ds/connected/EntitySelect").EntityKind;
}

export interface RelationSpec<P> {
  /**
   * Apply the spec to a FK column with optional context. Returns a keyed-less
   * `PresentationField` (the key is injected by `definePresentation` from the
   * property it's assigned to).
   */
  on(fk: AnyColumnRef, ctx?: RelationContext): PresentationField<P>;
}

/** Define a relation spec once; apply it to FKs with `.on(fk, ctx)`. */
export function relationSpec<P>(def: RelationSpecDef<P>): RelationSpec<P> {
  const targetCols = def.cols(db);
  return {
    on(fk, ctx = {}) {
      // Embed alias derived from the FK column (`company_id` → `company`); the
      // related record is read from `record[alias]` at render time. Callers
      // override via `ctx.alias` when the derived name collides with a scalar
      // column on the parent (PostgREST masks the scalar with the embed).
      const naturalAlias = relationAlias(fk.column);
      const alias = ctx.alias ?? naturalAlias;
      // The binding carries the fk column + target columns + (optional) alias
      // override so the select deriver, deps builder, and `data` lookup all
      // align on one name.
      const binding: RelationBinding = ctx.alias
        ? { fk, targetCols, alias }
        : { fk, targetCols };

      const field: PresentationField<P> = {
        __alias: alias,
        component: def.component,
        // Label namespace stays on the NATURAL alias (the FK-derived name) so
        // an `alias` override for PostgREST disambiguation doesn't ripple into
        // a new locale key that nobody added (e.g. an
        // `applicant_company_id` FK keeps the label `applicant_company`,
        // even when the PostgREST embed is renamed to `applicant_company_ref`
        // to dodge a scalar column collision).
        labelKey: fieldLabelKey(fk.table, naturalAlias),
        data: (record: Record<string, unknown>) => {
          const related = record[alias];
          return related != null ? def.data(related) : ({} as Partial<P>);
        },
        relation: binding,
        // Relation fields are filterable too — a multi-select entity picker on
        // the FK column. Label = the alias i18n key (translateFilters → t(label)).
        filterable: entitySelectFilter({
          name: fk.column,
          label: fieldLabelKey(fk.table, naturalAlias),
          kind: def.entityKind,
          multiple: true,
        }),
      };

      if (!ctx.readOnly) {
        const editField: Extract<EditFieldSpec, { type: "entity-select" }> = {
          type: "entity-select",
          kind: def.entityKind,
        };
        if (ctx.placeholder) editField.placeholder = ctx.placeholder;
        if (ctx.requireNip) editField.requireNip = ctx.requireNip;
        if (ctx.category) editField.category = ctx.category;
        if (ctx.role) editField.role = ctx.role;
        field.edit = {
          key: fk.column,
          field: editField,
          ...(ctx.required ? { required: true } : {}),
        };
      }

      return field;
    },
  };
}

// ─── The four canonical relation specs ─────────────────────────────────────

export const companyChip = relationSpec<CompanyChipProps>({
  component: CompanyChip,
  data: (c) => ({ company: c as CompanyChipProps["company"] }),
  cols: (d) => [d.companies.id, d.companies.short_name, d.companies.is_pending_review],
  entityKind: "company",
});

// Like companyChip (renders the same chip in list/KV contexts) but embeds the
// FULL company row a party CARD needs (address + identifiers). Reference this
// for transaction/invoice party companies so their PartyCard shows complete
// data; the extra columns are negligible in a list select.
//
// Also carries `latitude`/`longitude` — transaction party embeds (load place,
// unload place, transport company) need geocoded coordinates for map views.
//
// …and `categories`, for the same map views: `@ds/domain/CompanyCard` renders
// the category chips from it (the transactional map's marker popover shows a
// full card), and the map's "kategoria firmy" filter cuts on it client-side,
// so it has to travel with the embed rather than be fetched a second time.
export const companyParty = relationSpec<CompanyChipProps>({
  component: CompanyChip,
  data: (c) => ({ company: c as CompanyChipProps["company"] }),
  cols: (d) => [
    d.companies.id,
    d.companies.short_name,
    d.companies.is_pending_review,
    d.companies.nip,
    d.companies.country_code,
    d.companies.street,
    d.companies.zip_code,
    d.companies.city,
    d.companies.latitude,
    d.companies.longitude,
    d.companies.categories,
  ],
  entityKind: "company",
});

export const companyCard = relationSpec<CompanyCardProps>({
  component: CompanyCard,
  data: (c) => ({ company: c as CompanyCardProps["company"] }),
  cols: (d) => [
    d.companies.id,
    d.companies.short_name,
    d.companies.nip,
    d.companies.city,
    d.companies.categories,
  ],
  entityKind: "company",
});

export const productChip = relationSpec<ProductChipProps>({
  component: ProductChip,
  data: (p) => ({ product: p as ProductChipProps["product"] }),
  cols: (d) => [d.products.id, d.products.name, d.products.waste_code],
  entityKind: "product",
});

export const employeeChip = relationSpec<EmployeeChipProps>({
  component: EmployeeChip,
  data: (e) => ({ employee: e as EmployeeChipProps["employee"] }),
  cols: (d) => [d.employees.id, d.employees.full_name],
  entityKind: "employee",
});
